/**
 * SCR-028 — Portal Assignments /portal/assignments
 * List assignment cards with submission status. Student: submit via dialog.
 * Parent: read-only. Graded submissions: locked (no re-submit).
 * Mobile-first: 1-col card list; dialog full-screen on mobile.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { AlertCircle, RefreshCw, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { usePortalAssignments, usePortalAssignmentDetail } from '@client/lib/queries/use-portal';
import { useChildSelector } from '@client/store/child-selector';
import { authClient } from '@client/lib/auth-client';
import { SubmissionForm } from '@client/components/portal/submission-form';
import { useQueryClient } from '@tanstack/react-query';
import { portalKeys } from '@client/lib/queries/use-portal';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';

export const Route = createFileRoute('/portal/assignments')({
  component: PortalAssignments,
});

type StatusFilter = 'all' | 'pending' | 'submitted' | 'graded';

const STATUS_BADGE: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
  assigned: { label: 'Chưa nộp', variant: 'outline' },
  submitted: { label: 'Đã nộp', variant: 'default' },
  late: { label: 'Nộp muộn', variant: 'secondary' },
  graded: { label: 'Đã chấm', variant: 'default' },
};

const TYPE_LABEL: Record<string, string> = {
  homework: 'Bài tập', quiz: 'Kiểm tra', project: 'Dự án',
};

function PortalAssignments() {
  const { data: session } = authClient.useSession();
  const role = (session?.user as { role?: string })?.role ?? '';
  const isParent = role === 'parent';
  const { selectedChildId } = useChildSelector();
  const studentId = isParent ? selectedChildId : undefined;

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = usePortalAssignments(studentId, statusFilter);
  const qc = useQueryClient();

  const items = data?.items ?? [];

  if (isLoading) return <AssignmentsSkeleton />;

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 py-16 text-center">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">Không tải được bài tập</p>
        <Button variant="outline" size="sm" onClick={() => void refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />Thử lại
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      <h1 className="text-xl font-bold">Bài tập</h1>

      {/* Filter tabs */}
      <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
        <TabsList className="h-9 w-full sm:w-auto">
          <TabsTrigger value="all" className="text-xs flex-1 sm:flex-none px-3">Tất cả</TabsTrigger>
          <TabsTrigger value="pending" className="text-xs flex-1 sm:flex-none px-3">Chưa nộp</TabsTrigger>
          <TabsTrigger value="submitted" className="text-xs flex-1 sm:flex-none px-3">Đã nộp</TabsTrigger>
          <TabsTrigger value="graded" className="text-xs flex-1 sm:flex-none px-3">Đã chấm</TabsTrigger>
        </TabsList>
      </Tabs>

      {items.length === 0 && (
        <div className="py-16 text-center text-sm text-muted-foreground border border-dashed rounded-lg">
          {statusFilter === 'all'
            ? 'Chưa có bài tập nào được giao'
            : `Không có bài tập ${statusFilter === 'pending' ? 'chưa nộp' : statusFilter === 'submitted' ? 'đã nộp' : 'đã chấm'}`}
        </div>
      )}

      {/* Assignment card list — 1 col → 2 col → 3 col */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((item) => {
          const status = item.submission?.status ?? 'assigned';
          const badge = STATUS_BADGE[status] ?? STATUS_BADGE['assigned']!;
          const isOverdue =
            item.dueDate && new Date(item.dueDate) < new Date() && status === 'assigned';

          return (
            <Card
              key={item.assignmentId}
              className="border-border hover:bg-accent/30 transition-colors cursor-pointer"
              onClick={() => setSelectedAssignmentId(item.assignmentId)}
            >
              <CardContent className="px-4 py-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-sm leading-tight flex-1 min-w-0 line-clamp-2">
                    {item.title}
                  </p>
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                </div>

                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="outline" className="text-xs font-normal">
                    {TYPE_LABEL[item.type] ?? item.type}
                  </Badge>
                  <Badge variant={status === 'graded' ? 'success' : badge.variant} className="text-xs">
                    {badge.label}
                  </Badge>
                  {isOverdue && (
                    <Badge variant="destructive" className="text-xs">Quá hạn</Badge>
                  )}
                </div>

                <div className="text-xs text-muted-foreground space-y-0.5">
                  <p>{item.className}</p>
                  {item.dueDate && (
                    <p>
                      Hạn: {formatDistanceToNow(new Date(item.dueDate), { locale: vi, addSuffix: true })}
                    </p>
                  )}
                  {item.submission?.score != null && (
                    <p className="font-semibold text-foreground">
                      Điểm: {item.submission.score}{item.maxScore ? `/${item.maxScore}` : ''}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detail dialog */}
      {selectedAssignmentId && (
        <AssignmentDialog
          assignmentId={selectedAssignmentId}
          studentId={studentId}
          isParent={isParent}
          onClose={() => setSelectedAssignmentId(null)}
          onSubmitted={() => {
            void qc.invalidateQueries({ queryKey: portalKeys.assignments(studentId, statusFilter) });
            void qc.invalidateQueries({ queryKey: portalKeys.assignmentDetail(selectedAssignmentId, studentId) });
          }}
        />
      )}
    </div>
  );
}

function AssignmentDialog({
  assignmentId,
  studentId,
  isParent,
  onClose,
  onSubmitted,
}: {
  assignmentId: string;
  studentId?: string | null;
  isParent: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const { data, isLoading } = usePortalAssignmentDetail(assignmentId, studentId);

  const assignment = data?.assignment;
  const submission = data?.submission;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base leading-tight pr-6">
            {isLoading ? 'Đang tải...' : (assignment?.title ?? 'Bài tập')}
          </DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="space-y-3 py-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-20 w-full" />
          </div>
        )}

        {!isLoading && assignment && (
          <div className="space-y-4">
            {/* Assignment info */}
            <div className="space-y-2 text-sm">
              {assignment.description && (
                <div>
                  <p className="font-medium text-xs text-muted-foreground uppercase tracking-wide mb-1">Đề bài</p>
                  <p className="whitespace-pre-wrap text-foreground">{assignment.description}</p>
                </div>
              )}
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {assignment.dueDate && (
                  <span>Hạn nộp: {new Date(assignment.dueDate).toLocaleDateString('vi-VN')}</span>
                )}
                {assignment.maxScore != null && (
                  <span>Thang điểm: {assignment.maxScore}</span>
                )}
              </div>
            </div>

            {/* Existing submission info */}
            {submission && submission.status !== 'assigned' && (
              <div className="border border-border rounded-lg p-3 bg-muted/30 space-y-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Bài đã nộp</p>
                {submission.textContent && (
                  <p className="text-sm whitespace-pre-wrap">{submission.textContent}</p>
                )}
                {submission.files.length > 0 && (
                  <div className="space-y-1">
                    {submission.files.map((f) => (
                      <div key={f.id} className="flex items-center gap-2 text-xs">
                        <span className="truncate">{f.fileName}</span>
                        <span className="text-muted-foreground shrink-0">
                          ({(f.fileSize / 1024 / 1024).toFixed(1)} MB)
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {submission.score != null && (
                  <p className="text-sm font-semibold">
                    Điểm: {submission.score}{assignment.maxScore ? `/${assignment.maxScore}` : ''}
                    {submission.feedback && (
                      <span className="font-normal text-muted-foreground ml-2">— {submission.feedback}</span>
                    )}
                  </p>
                )}
              </div>
            )}

            {/* Submit form — student only, not graded */}
            {submission && (
              <SubmissionForm
                submissionId={submission.id}
                isParent={isParent}
                existingStatus={submission.status}
                existingTextContent={submission.textContent}
                onSuccess={() => {
                  onSubmitted();
                  onClose();
                }}
              />
            )}

            {!submission && !isParent && (
              <p className="text-sm text-muted-foreground text-center py-4">
                Đang tải thông tin bài nộp...
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AssignmentsSkeleton() {
  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      <Skeleton className="h-7 w-32" />
      <Skeleton className="h-9 w-64" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 rounded-lg" />)}
      </div>
    </div>
  );
}
